/**
 * WidgetEmbedder — dogfoods the real Hotline feedback widget inside the dashboard.
 *
 * Injects the same module `<script>` a customer would paste, loaded same-origin
 * from /vendor/hotline.js (served by the vite config). It renders on EVERY route
 * including the pre-auth login page — the widget's ingest is public, so no session
 * is needed — giving a "recursive" feedback loop: feedback about Hotline becomes
 * tickets in Hotline's own org.
 *
 * `data-api-base` is set EXPLICITLY: the bundle's cdn→api heuristic only fires for
 * a `cdn.*` script host, and ours is same-origin, so without it the widget would
 * POST feedback to the dashboard origin and 404. It's a no-op when no key is set,
 * and idempotent across React StrictMode's double-invoke (DOM-marker guard + the
 * widget's own `__HOTLINE_LOADED__` singleton).
 */
import { useEffect, type ReactElement } from 'react';

import { API_BASE_URL, HOTLINE_WIDGET_KEY } from '../config.js';

const EMBED_MARKER = 'data-hotline-embedder';

export interface WidgetScriptConfig {
  publicKey: string;
  apiBase: string;
}

/** Pure: the attributes for the injected widget `<script>` (unit-testable). */
export function buildWidgetScriptAttrs(cfg: WidgetScriptConfig): Record<string, string> {
  return {
    type: 'module',
    src: '/vendor/hotline.js',
    'data-public-key': cfg.publicKey,
    'data-api-base': cfg.apiBase,
    'data-mode': 'floating',
    [EMBED_MARKER]: '1',
    'data-testid': 'hotline-widget-script',
  };
}

export function WidgetEmbedder(): ReactElement | null {
  useEffect(() => {
    if (!HOTLINE_WIDGET_KEY) return;
    // Idempotent: never inject twice (StrictMode remount, or a re-render).
    if (document.querySelector(`script[${EMBED_MARKER}="1"]`)) return;

    const attrs = buildWidgetScriptAttrs({
      publicKey: HOTLINE_WIDGET_KEY,
      apiBase: API_BASE_URL,
    });
    const script = document.createElement('script');
    for (const [name, value] of Object.entries(attrs)) script.setAttribute(name, value);
    script.async = true;
    document.body.appendChild(script);
    // No cleanup: the widget claims a window singleton on boot; removing the tag
    // would not unmount it, and re-adding is a no-op. This is a one-shot injector.
  }, []);

  return null;
}
